// ======================================================
// COMMON VALIDATION FUNCTIONS
// ======================================================

// 1. NAME
export const validateName = (value) => {
  const name = value?.trim() || "";

  if (!name) {
    return "Name is required";
  }

  if (name.length < 2) {
    return "Name must be at least 2 characters";
  }

  if (name.length > 50) {
    return "Name must not exceed 50 characters";
  }

  if (!/^[A-Za-z ]+$/.test(name)) {
    return "Name can contain only letters and spaces";
  }

  return true;
};


// 2. EMAIL
export const validateEmail = (value) => {
  const email = value?.trim() || "";

  if (!email) {
    return "Email is required";
  }

const emailRegex =
  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

  if (!emailRegex.test(email)) {
    return "Please enter a valid email address";
  }

  return true;
};


// 3. MOBILE
export const validateMobile = (value) => {
  const mobile = value?.trim() || "";

  if (!mobile) {
    return "Mobile number is required";
  }

  const mobileRegex = /^[6-9][0-9]{9}$/;

  if (!mobileRegex.test(mobile)) {
    return "Mobile number must be exactly 10 digits and start with 6-9";
  }

  return true;
};


// 4. PASSWORD
export const validatePassword = (value) => {
  const password = value || "";

  if (!password) {
    return "Password is required";
  }

  if (password.length < 8) {
    return "Password must be at least 8 characters";
  }

  if (!/[A-Z]/.test(password)) {
    return "Password must contain at least one capital letter";
  }

  if (!/[a-z]/.test(password)) {
    return "Password must contain at least one small letter";
  }

  if (!/[^A-Za-z0-9]/.test(password)) {
    return "Password must contain at least one special character";
  }

  return true;
};

// ======================================================
// 5. OTP
// ======================================================
// Required
// Exactly 6 digits
// Numbers only
// ======================================================

export const validateOtp = (value) => {
  const otp = value?.trim() || "";

  if (!otp) {
    return "OTP is required";
  }

  if (!/^[0-9]{6}$/.test(otp)) {
    return "OTP must be exactly 6 digits";
  }

  return true;
};


// ======================================================
// 5. CITY
// ======================================================
// Required
// Only alphabets and spaces
// No numbers
// No special characters
// ======================================================

export const validateCity = (value) => {
  const city = value?.trim() || "";

  if (!city) {
    return "City is required";
  }

  if (!/^[A-Za-z ]+$/.test(city)) {
    return "City can contain only letters and spaces";
  }

  return true;
};


// ======================================================
// 6. PINCODE
// ======================================================
// Required
// Exactly 6 digits
// Numbers only
// ======================================================

export const validatePincode = (value) => {
  const pincode = value?.trim() || "";

  if (!pincode) {
    return "Pincode is required";
  }

  if (!/^[0-9]{6}$/.test(pincode)) {
    return "Pincode must be exactly 6 digits";
  }

  return true;
};


// ======================================================
// 7. FULL ADDRESS
// ======================================================
// Required
// Maximum 250 characters
// Letters, numbers and spaces only
// ======================================================

export const validateFullAddress = (value) => {
  const address = value?.trim() || "";

  if (!address) {
    return "Full address is required";
  }

  if (address.length > 250) {
    return "Full address must not exceed 250 characters";
  }

  // Allows letters, numbers, spaces and common address characters
  if (!/^[A-Za-z0-9\s,.\-/#'()]+$/.test(address)) {
    return "Full address contains invalid characters";
  }

  return true;
};


// ======================================================
// 8. SUBJECT
// ======================================================
// OPTIONAL
// Maximum 80 characters
// ======================================================

export const validateSubject = (value) => {
  const subject = value?.trim() || "";

  if (!subject) {
    return "Subject is required";
  }

  if (subject.length > 80) {
    return "Subject must not exceed 80 characters";
  }

  return true;
};


// ======================================================
// 9. MESSAGE
// ======================================================
// Required
// Maximum 250 characters
// ======================================================

export const validateMessage = (value) => {
  const message = value?.trim() || "";

  if (!message) {
    return "Message is required";
  }

  if (message.length > 250) {
    return "Message must not exceed 250 characters";
  }

  return true;
};


// ======================================================
// 10. REVIEW
// ======================================================
// Required
// Maximum 250 characters
// ======================================================

export const validateReview = (value) => {
  const review = value?.trim() || "";

  if (!review) {
    return "Review is required";
  }

  if (review.length > 250) {
    return "Review must not exceed 250 characters";
  }

  return true;
};


// ======================================================
// 11. GENERIC REQUIRED FIELD
// ======================================================

export const validateRequired = (
  value,
  fieldName = "This field"
) => {
  if (!value || !String(value).trim()) {
    return `${fieldName} is required`;
  }

  return true;
};


// ======================================================
// 12. VALIDATE COMPLETE FORM
// ======================================================
// Use this only when you want to manually validate
// a complete form object.
// ======================================================

export const validateForm = (formData) => {
  const errors = {};

  const nameError = validateName(formData.name);
  const emailError = validateEmail(formData.email);
  const mobileError = validateMobile(formData.mobile);
  const passwordError = validatePassword(formData.password);
  const cityError = validateCity(formData.city);
  const pincodeError = validatePincode(formData.pincode);
  const addressError = validateFullAddress(formData.fullAddress);
  const subjectError = validateSubject(formData.subject);
  const messageError = validateMessage(formData.message);
  const reviewError = validateReview(formData.review);

  if (nameError !== true) {
    errors.name = nameError;
  }

  if (emailError !== true) {
    errors.email = emailError;
  }

  if (mobileError !== true) {
    errors.mobile = mobileError;
  }

  if (passwordError !== true) {
    errors.password = passwordError;
  }

  if (cityError !== true) {
    errors.city = cityError;
  }

  if (pincodeError !== true) {
    errors.pincode = pincodeError;
  }

  if (addressError !== true) {
    errors.fullAddress = addressError;
  }

  if (subjectError !== true) {
    errors.subject = subjectError;
  }

  if (messageError !== true) {
    errors.message = messageError;
  }

  if (reviewError !== true) {
    errors.review = reviewError;
  }

  return errors;
};


// ======================================================
// BANNER / SLIDER VALIDATIONS
// ======================================================

// 13. BANNER IMAGE
// Required while creating.
// While editing, this validation is applied only if a new
// image is selected.
// Maximum file size: LESS THAN 2 MB
// ======================================================

export const validateBannerImage = (file) => {
  if (!file) {
    return "Banner image is required";
  }

  // 2 MB = 2 * 1024 * 1024 bytes
  const maxSize = 2 * 1024 * 1024;

  if (file.size >= maxSize) {
    return "Banner image must be less than 2 MB";
  }

  return true;
};


// ======================================================
// 14. BANNER TITLE
// Required
// Text only
// Maximum 100 characters
// ======================================================

export const validateBannerTitle = (value) => {
  const title = value?.trim() || "";

  if (!title) {
    return "Banner title is required";
  }

  if (title.length > 100) {
    return "Banner title must not exceed 100 characters";
  }

  // Allows letters, numbers, spaces and common text punctuation
  if (!/^[A-Za-z0-9 .,!?'"()&-]+$/.test(title)) {
    return "Banner title contains invalid characters";
  }

  return true;
};


// ======================================================
// 15. BANNER CONTENT
// Required
// Valid text
// Maximum 500 characters
//
// Removes HTML tags first because banner content may come
// from a rich text editor such as SunEditor.
// ======================================================

export const validateBannerContent = (value) => {
  const content = value || "";

  // Remove HTML tags
  const plainText = content
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!plainText) {
    return "Banner content is required";
  }

  if (plainText.length > 500) {
    return "Banner content must not exceed 500 characters";
  }

  // Allows normal text and common punctuation
  if (!/^[A-Za-z0-9 .,!?'"()&:%;+\-\/]+$/.test(plainText)) {
    return "Banner content contains invalid characters";
  }

  return true;
};


// ======================================================
// 16. STARTING PRICE
// Required
// Numbers only
// Maximum 2 digits after decimal
//
// Valid:
// 100
// 100.5
// 100.50
//
// Invalid:
// 100.555
// 100abc
// ₹100
// 100.5.5
// ======================================================

export const validateStartingPrice = (value) => {
  const price = String(value ?? "").trim();

  if (!price) {
    return "Starting price is required";
  }

  // Numbers with optional decimal part of maximum 2 digits
  const priceRegex = /^\d+(\.\d{1,2})?$/;

  if (!priceRegex.test(price)) {
    return "Starting price must contain only numbers with up to 2 decimal places";
  }

  return true;
};
// ======================================================
// CATEGORY / SEO VALIDATIONS
// ======================================================

export const validateSeoTitle = (value) => {
  const title = value?.trim() || "";

  if (!title) {
    return "SEO title is required";
  }

  if (title.length > 100) {
    return "SEO title must not exceed 100 characters";
  }

  if (!/^[A-Za-z0-9 .,!?'"()&:-]+$/.test(title)) {
    return "SEO title contains invalid characters";
  }

  return true;
};

export const validateSeoDescription = (value) => {
  const description = value?.trim() || "";

  if (!description) {
    return "SEO description is required";
  }

  if (description.length > 500) {
    return "SEO description must not exceed 500 characters";
  }

  if (!/^[A-Za-z0-9 .,!?'"()&:%;+\-\/]+$/.test(description)) {
    return "SEO description contains invalid characters";
  }

  return true;
};

export const validateSeoKeywords = (value) => {
  const keywords = value?.trim() || "";

  if (!keywords) {
    return "SEO keywords are required";
  }

  if (keywords.length > 250) {
    return "SEO keywords must not exceed 250 characters";
  }

  if (!/^[A-Za-z0-9 ,.-]+$/.test(keywords)) {
    return "SEO keywords contain invalid characters";
  }

  return true;
};

export const validateImageSize = (file) => {
  if (!file) {
    return true;
  }

  const maxSize = 2 * 1024 * 1024;

  if (file.size >= maxSize) {
    return "Image must be less than 2 MB";
  }

  return true;
};

// VALIDATE COST -SHIPPIN RULES 
export const validateCost = (value, fieldName = "Cost") => {
  const cost = String(value ?? "").trim();

  if (!cost) {
    return `${fieldName} is required`;
  }

  const costRegex = /^\d+(\.\d{1,2})?$/;

  if (!costRegex.test(cost)) {
    return `${fieldName} must contain only numbers with up to 2 decimal places`;
  }

  return true;
};


// ======================================================
// PRODUCT VALIDATIONS
// ======================================================

// 17. PRODUCT NAME
// Required
// Only alphabets and spaces
// Maximum 250 characters
// No numbers or special characters
// ======================================================

export const validateProductName = (value) => {
  const name = value?.trim() || "";

  if (!name) {
    return "Product name is required";
  }

  if (name.length > 250) {
    return "Product name must not exceed 250 characters";
  }

  if (!/^[A-Za-z ]+$/.test(name)) {
    return "Product name can contain only alphabets and spaces";
  }

  return true;
};


// ======================================================
// 18. PRODUCT IMAGE SIZE
// Maximum LESS THAN 2 MB
// ======================================================

export const validateProductImage = (file) => {
  if (!file) {
    return true;
  }

  const maxSize = 2 * 1024 * 1024;

  if (file.size >= maxSize) {
    return "Each product image must be less than 2 MB";
  }

  return true;
};


// ======================================================
// 19. PRODUCT QUANTITY
// Positive number only
// ======================================================

export const validateProductQty = (value) => {
  const qty = String(value ?? "").trim();

  if (!qty) {
    return "Quantity is required";
  }

  if (!/^\d+$/.test(qty)) {
    return "Quantity must contain only numbers";
  }

  if (Number(qty) <= 0) {
    return "Quantity must be greater than 0";
  }

  return true;
};


// ======================================================
// 20. PRODUCT TEXT
// Used for Short Description / Long Description
// Allows normal text and common punctuation
// ======================================================

export const validateProductText = (value, fieldName = "Description") => {
  const text = value
    ?.replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim() || "";

  if (!text) {
    return `${fieldName} is required`;
  }

  if (!/^[A-Za-z0-9 .,!?'"()&:%;+\-\/]+$/.test(text)) {
    return `${fieldName} contains invalid characters`;
  }

  return true;
};


// ======================================================
// 21. SKU FORMAT
// SKU required
// Allows letters, numbers, hyphen and underscore
// ======================================================

export const validateSku = (value) => {
  const sku = value?.trim() || "";

  if (!sku) {
    return "SKU is required";
  }

  if (!/^[A-Za-z0-9_-]+$/.test(sku)) {
    return "SKU can contain only letters, numbers, hyphens and underscores";
  }

  return true;
};


// ======================================================
// 22. PRODUCT PRICE
// Numbers only
// Maximum 2 decimal places
//
// Valid:
// 100
// 100.5
// 100.50
//
// Invalid:
// 100.555
// 100abc
// ₹100
// ======================================================

export const validateProductPrice = (value, fieldName = "Price") => {
  const price = String(value ?? "").trim();

  if (!price) {
    return `${fieldName} is required`;
  }

  const priceRegex = /^\d+(\.\d{1,2})?$/;

  if (!priceRegex.test(price)) {
    return `${fieldName} must contain only numbers with up to 2 decimal places`;
  }

  if (Number(price) < 0) {
    return `${fieldName} must be a positive number`;
  }

  return true;
};


// ======================================================
// 23. OFFER PRICE
// Numbers only
// Maximum 2 decimal places
// Must be less than Price
// ======================================================

export const validateOfferPrice = (offerPrice, price) => {
  const offer = String(offerPrice ?? "").trim();
  const productPrice = String(price ?? "").trim();

  // Offer price is optional
  if (!offer) {
    return true;
  }

  const priceRegex = /^\d+(\.\d{1,2})?$/;

  if (!priceRegex.test(offer)) {
    return "Offer price must contain only numbers with up to 2 decimal places";
  }

  if (!productPrice || !priceRegex.test(productPrice)) {
    return "Please enter a valid price before entering offer price";
  }

  if (Number(offer) >= Number(productPrice)) {
    return "Offer price must be less than price";
  }

  return true;
};


// ======================================================
// 24. OFFER START DATE
// Date required when offer price is provided
// ======================================================

export const validateOfferStartDate = (value) => {
  const date = String(value ?? "").trim();

  // Optional field
  if (!date) {
    return true;
  }

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return "Please select a valid offer start date";
  }

  return true;
};


// ======================================================
// 25. OFFER END DATE
// Must be after Offer Start Date
// ======================================================

export const validateOfferEndDate = (endDate, startDate) => {
  const end = String(endDate ?? "").trim();
  const start = String(startDate ?? "").trim();

  // Both dates are optional
  if (!end && !start) {
    return true;
  }

  // If end date is entered, start date is required
  if (end && !start) {
    return "Offer start date is required";
  }

  // If start date is entered but end date is empty
  if (start && !end) {
    return "Offer end date is required";
  }

  const startDateObj = new Date(start);
  const endDateObj = new Date(end);

  if (
    Number.isNaN(startDateObj.getTime()) ||
    Number.isNaN(endDateObj.getTime())
  ) {
    return "Please select valid offer dates";
  }

  if (endDateObj <= startDateObj) {
    return "Offer end date must be after offer start date";
  }

  return true;
};


// ======================================================
// 26. SEO PRODUCT TEXT
// Valid text
// ======================================================

export const validateProductSeoText = (
  value,
  fieldName = "SEO field"
) => {
  const text = value?.trim() || "";

  // SEO field is optional
  if (!text) {
    return true;
  }

  if (!/^[A-Za-z0-9 .,!?'"()&:%;+\-\/]+$/.test(text)) {
    return `${fieldName} contains invalid characters`;
  }

  return true;
};